import { ReactElement, useState, useEffect } from "react";
import {Button,Col,Drawer,Form,Input,Row,Select,Upload,message, Spin, Checkbox } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { InboxOutlined } from "@ant-design/icons";
import firebase from "firebase/app";
import "firebase/storage";
import { DrawerFooter } from "../../components/GlobalStyles";
import { EDIT_CLUB_REQUEST } from "../../redux/constants";
import { getAuthState, getUserState } from "../../redux/selectors";
import { countries, clubStatus, currencies} from "../../constants";

const { Option } = Select;
const { useForm } = Form;

type Props = {
  visible: boolean;
  onClose: () => void;
  club: any;
};

function beforeUpload(file: any) {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    message.error("You can only upload JPG/PNG file!");
  }
  return false;
}

export function EditClubDrawer({visible, onClose, club}: Props): ReactElement {
  const dispatch = useDispatch();
  const { currentUser } = useSelector(getAuthState);
  const { users } = useSelector(getUserState);
  const [form] = useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [currency, setCurrency] = useState<string>(club.currency ? club.currency : "AED");
  const [price30, setPrice30] = useState<string>(club.price[30]);
  const [price60, setPrice60] = useState<string>(club.price[60]);
  const [price90, setPrice90] = useState<string>(club.price[90]);
  const [price120, setPrice120] = useState<string>(club.price[120]);
  const [managers, setManagers] = useState<any[]>([]);
  const [checked, setChecked] = useState<boolean>(club.enable);

  const storage = firebase.storage();

  const onFinish = async (values: any) => {
    setLoading(true);
    const updatedFormValue = {
      ...values,
      key: club.key,
      clubId: club.clubId,
      firestoreRef : club.firestoreRef,
      currency,
      price: {
        30: price30,
        60: price60,
        90: price90,
        120: price120
      },
      // enable: checked,
      manager: values.manager === undefined ? [] : values.manager,
      bookingDatesRange: values.bookingDatesRange === "" || values.bookingDatesRange === 0 ? 2 : values.bookingDatesRange,
      payOverCount: values.payOverCount === "pay_over_count",
      currentUser,
    };
    if (values.photo) {
      const ref = `clubs/${values.name}/${values.photo.file.name}`;
      await storage.ref(ref).put(values.photo.file);
      const clubImageUrl = await storage.ref(ref).getDownloadURL();
      updatedFormValue.photo = clubImageUrl;
    } else {
      updatedFormValue.photo = club.photo;
    }

    Object.keys(updatedFormValue).forEach((key) => {
      if (updatedFormValue[key] === undefined) updatedFormValue[key] = "";
    });

    dispatch({
      type: EDIT_CLUB_REQUEST,
      payload: updatedFormValue,
    });
    form.resetFields();
    setLoading(false);
    onClose();
  };

  const onFinishFailed = (errorInfo: any) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  useEffect(() => {
    const mManagers = users.filter((user) => user.role === "manager" && user.club === "");
    const managerIds = club.manager || [];
    const clubManagers = managerIds.map((m: string) => users.find((user) => user.id === m && user.role === "manager"));
    setManagers([...mManagers, ...clubManagers ]);
    setLoading(false);
  }, [users]);

  const onChangePriceCurreny = (value: string) => {
    setCurrency(value);
  };

  const toggleCheck = () => {
    setChecked(!checked);
  }

  return (
    <Drawer
      title={t("club.edit_club")}
      width={720}
      onClose={onClose}
      visible={visible}
      bodyStyle={{ paddingBottom: 80 }}
    >
      {!loading && <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          name: club.name,
          manager: club.manager === null ? [] : club.manager,
          country: club.country,
          description: club.description,
          website: club.website.replace("https://", "").replace(".com", ""),
          contactEmail: club.contactEmail,
          contactPhone: club.contactPhone,
          facebook: club.facebook,
          instagram: club.instagram,
          linkedIn: club.linkedIn,
          twitter: club.twitter,
          price: club.price,
          status: club.status,
          show: club.show,
          bookingDatesRange: club.bookingDatesRange,
          payOverCount: club.payOverCount ? "pay_over_count" : "online"
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="name" label={t("club.club_name")}
              rules={[
                {
                  required: true,
                  message: t("club.club_name_placeholder"),
                },
              ]}
            >
              <Input placeholder={t("club.club_name_placeholder")} autoComplete="off"/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="manager" label={t("club.club_manager")}>
              <Select placeholder={t("club.club_manager_placeholder")} mode="multiple" allowClear>
                {managers.map((manager) => {
                  return (
                    <Option key={manager.id} value={manager.id}>
                      {manager.fullName}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
          
        </Row>
       
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item name="photo" label="Photo">
              <Upload.Dragger
                name="files"
                beforeUpload={beforeUpload}
                showUploadList
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint"> Support for a single upload.</p>
              </Upload.Dragger>
            </Form.Item>
          </Col>
        </Row>
        {/* Country and Curreny */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="country" label={t("club.location")} >
              <Select placeholder={t("club.location_placeholder")}>
                {countries.map(({code, country}) => (
                  <Option key={code} value={code}>{country}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="currency" label={t("club.currency")}>
              <Select
                defaultValue={club.currency}
                className="select-before"
                onChange={onChangePriceCurreny}
              >
              {currencies.map( c => <Option value={c.value}>{c.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* Pricing */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label={t("club.price")}>
              <Input
                style={{ width: "50%" }}
                placeholder={t("club.price_placeholder")}
                addonBefore={<span>30 Mins</span>}
                onChange={(event) => setPrice30(event.target.value)}
                type="number"
                defaultValue={price30}
              />
              <Input
                style={{ width: "50%", }}
                placeholder={t("club.price_placeholder")}
                addonBefore={<span>60 Mins</span>}
                onChange={(event) => setPrice60(event.target.value)}
                type="number"
                defaultValue={price60}
              />
              <Input
                style={{ width: "50%", marginTop : 10 }}
                placeholder={t("club.price_placeholder")}
                addonBefore={<span>90 Mins</span>}
                onChange={(event) => setPrice90(event.target.value)}
                type="number"
                defaultValue={price90}
              />
              <Input
                style={{ width: "50%", marginTop : 10 }}
                placeholder={t("club.price_placeholder")}
                addonBefore={<span>120 Mins</span>}
                onChange={(event) => setPrice120(event.target.value)}
                type="number"
                defaultValue={price120}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="payOverCount" label={t("club.payment_method")}>
              <Select placeholder={t("club.payment_method_placeholder")}>
                <Option value="pay_over_count">Pay Over Count</Option>
                <Option value="online">Online</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* Website */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="website"
              label={t("club.website")}
              rules={[
                {
                  required: true,
                  message: t("club.website_placeholder"),
                },
              ]}
            >
              <Input
                style={{ width: "100%" }}
                addonBefore="https://"
                placeholder={t("club.website_placeholder")}
                autoComplete="off"
              />
            </Form.Item>
          </Col>
        </Row>
        {/* Contact Email and Phone Number */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="contactEmail"
              label={t("club.contact_email")}
              rules={[
                {
                  type: "email",
                  message: "Please enter valid email address",
                },
              ]}
            >
              <Input placeholder={t("club.contact_email")} autoComplete="off"/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="contactPhone" label={t("club.contact_number")}>
              <Input placeholder={t("club.contact_number")} autoComplete="off"/>
            </Form.Item>
          </Col>
        </Row>
        {/* Social Links */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="facebook" label={t("club.facebook")}>
              <Input placeholder={t("club.facebook")} autoComplete="off"/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="instagram" label={t("club.instagram")}>
              <Input placeholder={t("club.instagram")} autoComplete="off"/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="linkedIn" label={t("club.linkedIn")}>
              <Input placeholder={t("club.linkedIn")} autoComplete="off"/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="twitter" label={t("club.twitter")}>
              <Input placeholder={t("club.twitter")} autoComplete="off"/>
            </Form.Item>
          </Col>
        </Row>
       {/* Clug Status */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="status" label="Club Status">
              <Select
                placeholder="Please select club status"
              >
                {clubStatus.map(({status, label }) => (
                  <Option key={status} value={status}>{label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="show" label="Hide/Show">
              <Select
                placeholder="Please select club status"
              >
                <Option value="show">Show</Option>
                <Option value="hide">Hide</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="bookingDatesRange" 
              label="Booking Date Range"
            >
              <Input placeholder="Please enter the number of dates" type="number" min={2} max={30} />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={t("club.description")}
              rules={[
                {
                  required: true,
                  message: t("club.description_placeholder"),
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder={t("club.description_placeholder")}
                autoComplete="off"
              />
            </Form.Item>
          </Col>
        </Row>

        <DrawerFooter>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            {t("common.cancel")}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t("common.submit")}
          </Button>
        </DrawerFooter>
      </Form> }
    </Drawer> 
  );
}
